import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  registerRecordQuery,
  useRegisterActions,
  type RegisterField,
  type RegisterKind,
  type RegisterRecord,
} from '@/features/registers/api';
import { isApiError } from '@/lib/api';
import { toApiData, toFormValue, type FormValue } from './register-format';

/** A problem shown in the Correct form; `field: ''` = above the fields. */
export type CorrectError = { field: string; message: string };

const isEmpty = (v: FormValue | undefined) =>
  v === undefined || (Array.isArray(v) ? !v.length : v === '');

const same = (a: FormValue, b: FormValue) =>
  Array.isArray(a) && Array.isArray(b)
    ? [...a].sort().join('\n') === [...b].sort().join('\n')
    : typeof a === 'string' && typeof b === 'string'
      ? a.trim() === b.trim()
      : a === b;

/**
 * RL-2018: the fields a correction changes, as the API's typed values. Only
 * changed fields are sent (PATCH keeps the others). The API ignores an empty
 * value there, so a correction cannot empty a field: that is refused here.
 */
export function correctionData(
  fields: RegisterField[],
  stored: Record<string, unknown>,
  values: Record<string, FormValue>,
): { data: Record<string, unknown>; error?: CorrectError } {
  const changed = fields.filter(
    (f) =>
      !same(
        toFormValue(f, stored[f.key] ?? null),
        values[f.key] ?? toFormValue(f, null),
      ),
  );
  if (!changed.length)
    return {
      data: {},
      error: { field: '', message: 'Change at least one field.' },
    };
  const emptied = changed.find(
    (f) => f.type !== 'boolean' && isEmpty(values[f.key]),
  );
  if (emptied)
    return {
      data: {},
      error: {
        field: emptied.key,
        message: emptied.required
          ? `${emptied.label} is required.`
          : 'A correction cannot empty a field. Enter the right value or leave it as it was.',
      },
    };
  const { data, invalid } = toApiData(changed, values);
  const bad = changed.find((f) => f.label === invalid);
  if (bad) return { data, error: { field: bad.key, message: 'Check this.' } };
  return { data };
}

export const reasonOk = (reason: string) =>
  reason.trim().length >= 3 && reason.trim().length <= 500;

/** The record as it was when the form opened (or last reloaded). */
type Base = Pick<RegisterRecord, 'id' | 'data' | 'version'>;

/**
 * RL-2018: form values after someone else's change: fields you did not edit
 * take the new stored value; your edits stay.
 */
export function mergeAfterConflict(
  fields: RegisterField[],
  was: Record<string, unknown>,
  now: Record<string, unknown>,
  values: Record<string, FormValue>,
): Record<string, FormValue> {
  return Object.fromEntries(
    fields.map((f) => {
      const before = toFormValue(f, was[f.key] ?? null);
      const mine = values[f.key] ?? before;
      return [
        f.key,
        same(mine, before) ? toFormValue(f, now[f.key] ?? null) : mine,
      ];
    }),
  );
}

export const CONFLICT_MESSAGE =
  'Someone else changed this record while you were correcting it. The form now shows their change, with your edits kept. Check it and save again.';

/**
 * RL-2018: state of the drawer's "Correct" form. The values and the version
 * are those of the record when Correct was pressed, so a change made
 * meanwhile is refused (VERSION_CONFLICT), never overwritten. `fail` shows an
 * error the form cannot place (no permission, network).
 */
export function useCorrectForm(
  k: RegisterKind,
  r: RegisterRecord | undefined,
  fail: (err: unknown) => void,
) {
  const qc = useQueryClient();
  const { correct } = useRegisterActions(k.kind);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, FormValue>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState<CorrectError | null>(null);
  const [base, setBase] = useState<Base | null>(null);
  const start = () => {
    if (!r) return;
    setBase({ id: r.id, data: r.data, version: r.version });
    setValues(
      Object.fromEntries(
        k.fields.map((f) => [f.key, toFormValue(f, r.data[f.key] ?? null)]),
      ),
    );
    setReason('');
    setError(null);
    setOpen(true);
  };
  const setValue = (key: string, v: FormValue) => {
    setValues((s) => ({ ...s, [key]: v }));
    setError((e) => (e?.field === key ? null : e));
  };
  /** Resolves true when the correction was saved. */
  const submit = async (): Promise<boolean> => {
    if (!base) return false;
    const { data, error: invalid } = correctionData(
      k.fields,
      base.data,
      values,
    );
    if (invalid) {
      setError(invalid);
      return false;
    }
    try {
      await correct.mutateAsync({
        id: base.id,
        data,
        reason: reason.trim(),
        version: base.version,
      });
      setOpen(false);
      return true;
    } catch (err) {
      if (isApiError(err) && err.code === 'RECORD_INVALID') {
        const f = err.problem?.field;
        setError({
          field: typeof f === 'string' ? f : '',
          message: err.message,
        });
      } else if (isApiError(err) && err.code === 'VERSION_CONFLICT') {
        // Reload underneath and keep what was typed (values and reason).
        try {
          const now = await qc.fetchQuery({
            ...registerRecordQuery(k.kind, base.id),
            staleTime: 0,
          });
          setValues((v) =>
            mergeAfterConflict(k.fields, base.data, now.data, v),
          );
          setBase({ id: now.id, data: now.data, version: now.version });
          setError({ field: '', message: CONFLICT_MESSAGE });
        } catch (reload) {
          fail(reload);
        }
      } else fail(err);
      return false;
    }
  };
  return {
    open,
    start,
    cancel: () => setOpen(false),
    values,
    setValue,
    reason,
    setReason,
    error,
    submit,
    pending: correct.isPending,
  };
}

export type CorrectForm = ReturnType<typeof useCorrectForm>;
